import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import DataTable from 'react-data-table-component'
import moment from 'moment'
import { Link, useNavigate } from 'react-router-dom'
import { useModuleSettings } from '../../context/adminSettingsContext'
import { hasPermission } from '../../common/permissions'
import Swal from 'sweetalert2'
import { allSubscriptionPlans, changeSubscriptionPlanStatus } from '../../slice/subscriptionPlanSlice'
import { deleteSubscriptionPlan } from '../../apis/apiHandler'

const SubscriptionPlanList = () => {
    const dispatch = useDispatch()
    const { subscriptionPlanList } = useSelector(state => state.subscriptionPlan)
    const navigate = useNavigate()

    const {
        page,
        setPage,
        limit,
        setLimit,
    } = useModuleSettings()

    const [statusFilter, setStatusFilter] = useState('all')
    const [searchQuery, setSearchQuery] = useState('')
    const [searchDebounce, setSearchDebounce] = useState('')

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchDebounce(searchQuery)
            setPage(1)
        }, 500)

        return () => clearTimeout(timer)
    }, [searchQuery])

    useEffect(() => {
        dispatch(allSubscriptionPlans({
            page,
            limit,
            filter: statusFilter !== 'all' ? statusFilter : undefined,
            search: searchDebounce || undefined
        }))
    }, [dispatch, page, limit, statusFilter, searchDebounce])

    // Toggle status handler
    const handleToggleStatus = async (planId) => {
        try {
            const response = await dispatch(changeSubscriptionPlanStatus({
                id: planId
            }))
            if (response?.payload?.code == 200) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    backdrop: false,
                    title: response?.payload?.message,
                    showConfirmButton: false,
                    timer: 1500,
                })
            }
            dispatch(allSubscriptionPlans({
                page,
                limit,
                filter: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchDebounce || undefined
            }))
        } catch (error) {
            console.error('Error toggling subscription plan status:', error)
        }
    }

    // Delete handler
    const handleDelete = async (planId) => {
        const result = await Swal.fire({
            title: 'Delete Subscription Plan?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        })

        if (!result.isConfirmed) return

        try {
            const deleteResponse = await dispatch(deleteSubscriptionPlan({
                id: planId
            }))

            if (deleteResponse?.payload?.code == 200) {
                Swal.fire({
                    icon: 'success',
                    title: 'Deleted',
                    text: deleteResponse?.payload?.message,
                    timer: 1500,
                    showConfirmButton: false,
                })
            }

            dispatch(
                allSubscriptionPlans({
                    page,
                    limit,
                    filter: statusFilter !== 'all' ? statusFilter : undefined,
                    search: searchDebounce || undefined
                })
            )
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Failed',
                text: error || 'Failed to delete subscription plan.',
            })
        }
    }

    const handleView = (planId) => {
        navigate('/subscriptions/view', {
            state: { planId }
        })
    }

    const handleEdit = (planId) => {
        navigate('/subscriptions/edit', {
            state: { planId }
        })
    }

    // Get duration badge
    const getDurationBadge = (durationType) => {
        const durationConfig = {
            monthly: { bg: '#17a2b8', text: 'Monthly' },
            quarterly: { bg: '#ffc107', text: 'Quarterly' },
            yearly: { bg: '#28a745', text: 'Yearly' },
            lifetime: { bg: '#6f42c1', text: 'Lifetime' }
        }

        const config = durationConfig[durationType] || { bg: '#6c757d', text: durationType }

        return (
            <span
                style={{
                    backgroundColor: config.bg,
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'capitalize',
                    display: 'inline-block'
                }}
            >
                {config.text}
            </span>
        )
    }

    // Get price badge styling
    const getPriceBadge = (price) => {
        if (price === 0 || price === '0') {
            return (
                <span
                    style={{
                        fontWeight: '700',
                        color: '#28a745',
                        fontSize: '16px',
                        backgroundColor: '#d4edda',
                        padding: '6px 12px',
                        borderRadius: '6px',
                        border: '1px solid #c3e6cb'
                    }}
                >
                    FREE
                </span>
            )
        }
        return (
            <span
                style={{
                    fontWeight: '700',
                    fontSize: '16px',
                    color: '#495057'
                }}
            >
                AED {parseFloat(price).toFixed(2)}
            </span>
        )
    }

    const columns = [
        {
            name: 'Sr No',
            width: '80px',
            selector: (_, index) =>
                ((subscriptionPlanList?.paging?.page_no || 1) - 1) * limit + index + 1,
        },
        {
            name: 'Plan ID',
            selector: row => row.id,
            sortable: true,
            width: '100px',
        },
        {
            name: 'Plan Name',
            selector: row => row.name,
            sortable: true,
            minWidth: '180px',
            cell: row => (
                <div>
                    <div style={{ fontWeight: '600', fontSize: '13px' }}>
                        {row.name || 'NA'}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6c757d' }}>
                        {row.duration_days} days
                    </div>
                </div>
            )
        },
        // {
        //     name: 'Description',
        //     selector: row => row.description || 'NA',
        //     sortable: true,
        //     minWidth: '280px',
        //     cell: row => (
        //         <div
        //             style={{
        //                 overflow: 'hidden',
        //                 textOverflow: 'ellipsis',
        //                 whiteSpace: 'nowrap'
        //             }}
        //             title={row.description}
        //         >
        //             {row.description || 'NA'}
        //         </div>
        //     )
        // },
        {
            name: 'Duration',
            width: '130px',
            center: true,
            cell: row => getDurationBadge(row.duration_type)
        },
        {
            name: 'Price',
            selector: row => row.price,
            sortable: true,
            width: '140px',
            center: true,
            cell: row => getPriceBadge(row.price)
        },
        {
            name: 'Apple Product ID',
            selector: row => row.apple_product_id || 'NA',
            minWidth: '200px',
            cell: row => (
                <div
                    style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#495057',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={row.apple_product_id}
                >
                    {row.apple_product_id || 'NA'}
                </div>
            )
        },
        {
            name: 'Google Product ID',
            selector: row => row.google_product_id || 'NA',
            minWidth: '200px',
            cell: row => (
                <div
                    style={{
                        fontFamily: 'monospace',
                        fontSize: '11px',
                        color: '#495057',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                    title={row.google_product_id}
                >
                    {row.google_product_id || 'NA'}
                </div>
            )
        },
        {
            name: 'Created Date',
            selector: row =>
                row.createdAt
                    ? moment(row.createdAt).local().format('DD MMM YYYY')
                    : 'NA',
            sortable: true,
            minWidth: '140px',
        },
        {
            name: 'Status',
            width: '100px',
            center: true,
            cell: row => (
                <div className="form-check form-switch">
                    <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`switch-${row.id}`}
                        checked={row.is_active === 1}
                        onChange={() => handleToggleStatus(row.id)}
                        disabled={!hasPermission('subscriptions', 'edit') || row.is_deleted === 1}
                        style={{
                            cursor: hasPermission('subscriptions', 'edit') && row.is_deleted !== 1 ? 'pointer' : 'not-allowed',
                            opacity: row.is_deleted === 1 ? 0.5 : 1
                        }}
                    />
                </div>
            ),
        },
        {
            name: 'Action',
            width: '150px',
            center: true,
            cell: row => (
                <div className="d-flex gap-2 justify-content-center">
                    {hasPermission('subscriptions', 'view') && (
                        <button
                            className="btn btn-sm btn-outline-primary"
                            title="View"
                            onClick={() => handleView(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-eye" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('subscriptions', 'edit') && (
                        <button
                            className="btn btn-sm btn-outline-warning"
                            title="Edit"
                            onClick={() => handleEdit(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-edit" style={{ fontSize: '14px' }} />
                        </button>
                    )}

                    {hasPermission('subscriptions', 'delete') && row.is_deleted !== 1 && (
                        <button
                            className="btn btn-sm btn-outline-danger"
                            title="Delete"
                            onClick={() => handleDelete(row.id)}
                            style={{
                                width: '32px',
                                height: '32px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dc3545',
                                borderRadius: '4px'
                            }}
                        >
                            <i className="fe-trash-2" style={{ fontSize: '14px' }} />
                        </button>
                    )}
                </div>
            ),
        },
    ]

    const customStyles = {
        table: {
            style: {
                backgroundColor: '#ffffff',
                borderRadius: '8px',
            },
        },
        headRow: {
            style: {
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6',
                minHeight: '48px',
            },
        },
        headCells: {
            style: {
                fontSize: '13px',
                fontWeight: '600',
                color: '#495057',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
                color: '#212529',
                paddingTop: '12px',
                paddingBottom: '12px',
                paddingLeft: '12px',
                paddingRight: '12px',
            },
        },
        rows: {
            style: {
                minHeight: '60px',
                '&:hover': {
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer',
                },
            },
            stripedStyle: {
                backgroundColor: '#ffffff',
            },
        },
        pagination: {
            style: {
                borderTop: '1px solid #dee2e6',
                minHeight: '56px',
                fontSize: '13px',
            },
        },
    }

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0 fw-semibold">Subscription Plans</h4>

                {hasPermission('subscriptions', 'add') && (
                    <Link
                        to="/subscriptions/add"
                        className="btn btn-success"
                        style={{
                            padding: '8px 20px',
                            fontSize: '14px',
                            fontWeight: '500',
                            borderRadius: '6px'
                        }}
                    >
                        + Add Plan
                    </Link>
                )}
            </div>

            {/* Search and Filter */}
            <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: '8px' }}>
                <div className="card-body p-3">
                    <div className="row g-3">
                        <div className="col-md-8">
                            <div className="position-relative">
                                <i
                                    className="fe-search position-absolute"
                                    style={{
                                        left: '12px',
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: '#6c757d',
                                        fontSize: '16px'
                                    }}
                                />
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by plan name, description, or product ID..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    style={{
                                        paddingLeft: '38px',
                                        paddingRight: searchQuery ? '38px' : '12px',
                                        height: '40px',
                                        fontSize: '14px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '6px'
                                    }}
                                />
                                {searchQuery && (
                                    <button
                                        className="btn btn-link position-absolute p-0"
                                        type="button"
                                        onClick={() => setSearchQuery('')}
                                        style={{
                                            right: '12px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            color: '#6c757d'
                                        }}
                                    >
                                        <i className="fe-x" style={{ fontSize: '16px' }}></i>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="col-md-4">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => {
                                    setStatusFilter(e.target.value)
                                    setPage(1)
                                }}
                                style={{
                                    height: '40px',
                                    fontSize: '14px',
                                    border: '1px solid #dee2e6',
                                    borderRadius: '6px'
                                }}
                            >
                                <option value="all">All</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                                <option value="deleted">Deleted</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="card border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                <DataTable
                    columns={columns}
                    data={subscriptionPlanList?.data || []}
                    pagination
                    paginationServer
                    paginationTotalRows={subscriptionPlanList?.paging?.total || 0}
                    paginationPerPage={limit}
                    paginationDefaultPage={subscriptionPlanList?.paging?.page_no || 1}
                    onChangeRowsPerPage={(newLimit) => {
                        setLimit(newLimit)
                        setPage(1)
                    }}
                    onChangePage={(newPage) => setPage(newPage)}
                    responsive
                    highlightOnHover
                    persistTableHead
                    progressPending={subscriptionPlanList?.loading}
                    noDataComponent={
                        <div className="p-5 text-center">
                            <i className="fe-credit-card" style={{ fontSize: '48px', color: '#adb5bd' }}></i>
                            <h6 className="mt-3 mb-1 text-muted">
                                {searchQuery ? 'No subscription plans found matching your search' : 'No subscription plans found'}
                            </h6>
                            <p className="text-muted small mb-0">
                                {searchQuery ? 'Try adjusting your search criteria' : 'Get started by adding a new subscription plan'}
                            </p>
                        </div>
                    }
                    customStyles={customStyles}
                />
            </div>
        </div>
    )
}

export default SubscriptionPlanList